import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const SLIDE_VARIANTS = {
  enter: (dir) => ({ x: dir > 0 ? "100%" : "-100%", opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? "-100%" : "100%", opacity: 0 }),
};

export default function PhotoMosaic({ images = [], onPhotoClick }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const stripRef = useRef(null);
  const touchStart = useRef(null);

  // Свайп по ленте миниатюр (твой код — сохранён)
  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    let startX = 0;
    let startScroll = 0;
    const onStart = (e) => {
      startX = e.touches[0].clientX;
      startScroll = el.scrollLeft;
    };
    const onMove = (e) => {
      const dx = startX - e.touches[0].clientX;
      el.scrollLeft = startScroll + dx;
      e.preventDefault();
    };
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
    };
  }, []);

  if (!images.length) return null;

  const mainImage = images[activeIndex] || images[0];
  const gridImages = images.slice(1, 6);
  const overlayImage = images[6] || images[images.length - 1];
  const remainingCount = Math.max(0, images.length - 7);

  const go = (dir) => {
    setDirection(dir);
    setActiveIndex((i) => (i + dir + images.length) % images.length);
  };

  const selectIndex = (idx) => {
    setDirection(idx > activeIndex ? 1 : -1);
    setActiveIndex(idx);
  };

  // Свайп по главному фото (мобильный)
  const handleTouchStart = (e) => {
    touchStart.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
  };
  const handleTouchEnd = (e) => {
    if (!touchStart.current || images.length <= 1) return;
    const dx = e.changedTouches[0].clientX - touchStart.current.x;
    const dy = e.changedTouches[0].clientY - touchStart.current.y;
    touchStart.current = null;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx > 0) go(-1);
      else go(1);
    }
  };

  return (
    <>
      {/* Desktop / tablet mosaic */}
      <div
        className="hidden sm:block w-full"
        style={{ aspectRatio: "16 / 9", maxHeight: "62vh" }}
      >
        <div className="flex gap-2 h-full">
          {/* Left: large image — клик открывает PhotoViewer */}
          <button
            className="w-1/2 relative overflow-hidden rounded-xl group"
            onClick={() => onPhotoClick(activeIndex)}
          >
            <img
              src={mainImage}
              alt="Фото отеля"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </button>

          {/* Right: 2×3 grid — клик делает миниатюру главной */}
          <div className="w-1/2 grid grid-cols-2 grid-rows-3 gap-2">
            {gridImages.map((img, idx) => {
              const imgIdx = idx + 1;
              const isActive = imgIdx === activeIndex;
              return (
                <button
                  key={idx}
                  className={`relative overflow-hidden rounded-lg group transition-all ${
                    isActive ? "ring-2 ring-blue-500 ring-offset-1" : ""
                  }`}
                  onClick={() => selectIndex(imgIdx)}
                >
                  <img
                    src={img}
                    alt={`Фото ${idx + 2}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </button>
              );
            })}

            {/* Last cell: overlay +N фото — клик открывает PhotoViewer */}
            <button
              className="relative overflow-hidden rounded-lg group"
              onClick={() => onPhotoClick(6)}
            >
              <img
                src={overlayImage}
                alt="Все фото"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/45 flex items-center justify-center transition-colors group-hover:bg-black/35">
                <span className="text-white font-bold text-lg">
                  +{remainingCount} фото
                </span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile: main image (swipeable) + thumbnail strip */}
      <div className="sm:hidden">
        <div
          className="relative w-full overflow-hidden rounded-xl"
          style={{ aspectRatio: "16 / 10" }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <AnimatePresence initial={false} custom={direction}>
            <motion.img
              key={activeIndex}
              src={mainImage}
              alt="Фото отеля"
              custom={direction}
              variants={SLIDE_VARIANTS}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.35, ease: "easeInOut" }}
              onClick={() => onPhotoClick(activeIndex)}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </AnimatePresence>

          {/* Counter */}
          <div className="absolute bottom-3 right-3 bg-black/50 backdrop-blur-sm text-white text-sm font-medium px-3 py-1.5 rounded-full pointer-events-none">
            {images.length} фото
          </div>

          {/* Dots */}
          {images.length > 1 && (
            <div className="absolute bottom-3 left-3 flex gap-1 pointer-events-none">
              {images.map((_, i) => (
                <div
                  key={i}
                  className={`rounded-full transition-all ${
                    i === activeIndex
                      ? "bg-white w-2 h-2"
                      : "bg-white/50 w-1.5 h-1.5"
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Thumbnail strip — все images, включая первую */}
        <div
          ref={stripRef}
          className="photo-strip"
          style={{
            marginTop: 8,
            width: "100%",
            height: 68,
            overflowX: "auto",
            overflowY: "hidden",
            WebkitOverflowScrolling: "touch",
            whiteSpace: "nowrap",
            display: "block",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          {images.map((img, idx) => {
            const isActive = idx === activeIndex;
            return (
              <button
                key={idx}
                onClick={() => selectIndex(idx)}
                style={{
                  display: "inline-block",
                  width: 96,
                  height: 68,
                  marginRight: 8,
                  padding: 0,
                  border: isActive
                    ? "2px solid #3b82f6"
                    : "2px solid transparent",
                  borderRadius: 8,
                  overflow: "hidden",
                  verticalAlign: "top",
                }}
              >
                <img
                  src={img}
                  alt={`Фото ${idx + 1}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              </button>
            );
          })}
        </div>

        <style>{`
          .photo-strip::-webkit-scrollbar {
            display: none;
            width: 0;
            height: 0;
          }
        `}</style>
      </div>
    </>
  );
}
